/**
 * PRICING SERVICE - Marketplace Price Calculations
 * 
 * NEW SYSTEM (2026-02-06):
 * - Cost: Read-only from CSV
 * - Price: Manual user input (what seller wants to receive after fees)
 * - Marketplace Listing Price: Calculated to achieve desired net price after fees
 * 
 * Calculates listing prices for:
 * - Mercado Livre: Clássico (11.5% + R$6.25) and Premium (16.5% + R$6.25)
 * - Shopee: 14% + R$3
 * - Amazon: 15% + R$5
 */

// Marketplace fee structures
const MARKETPLACE_FEES = {
  shopee: {
    commission: 0.14, // 14%
    fixedFee: 3.00, // R$3 per transaction
    name: 'Shopee'
  },
  mercadolivre: {
    classico: {
      commission: 0.115, // 11.5%
      fixedFee: 6.25, // R$6.25 per transaction
      name: 'Mercado Livre Clássico'
    },
    premium: {
      commission: 0.165, // 16.5%
      fixedFee: 6.25, // R$6.25 per transaction
      name: 'Mercado Livre Premium'
    }
  },
  amazon: {
    commission: 0.15, // 15%
    fixedFee: 5.00, // R$5 per transaction
    name: 'Amazon'
  }
};

/**
 * Calculate marketplace listing price from desired net price
 * Formula: listingPrice = (desiredNetPrice + fixedFee) / (1 - commission%)
 * 
 * @param {number} desiredNetPrice - What seller wants to receive after fees
 * @param {number} cost - Product cost (for profit calculation)
 * @param {Object} fees - Marketplace fee structure {commission, fixedFee}
 * @returns {Object} Pricing breakdown
 */
function calculateMarketplacePrice(desiredNetPrice, cost, fees) {
  const netPrice = parseFloat(desiredNetPrice) || 0;
  const costValue = parseFloat(cost) || 0;
  
  if (netPrice === 0) {
    return {
      listingPrice: 0,
      netPrice: 0,
      cost: costValue,
      commissionAmount: 0,
      fixedFee: fees?.fixedFee || 0,
      totalFees: 0,
      profit: 0,
      margin: 0
    };
  }
  
  // Calculate listing price needed to achieve desired net price
  const listingPrice = (netPrice + fees?.fixedFee) / (1 - fees?.commission);
  const commissionAmount = listingPrice * fees?.commission;
  const totalFees = commissionAmount + fees?.fixedFee;
  const profit = netPrice - costValue;
  const margin = netPrice > 0 ? (profit / netPrice) * 100 : 0;
  
  return {
    listingPrice: Math.max(0, listingPrice),
    netPrice,
    cost: costValue,
    commissionRate: fees?.commission * 100,
    commissionAmount,
    fixedFee: fees?.fixedFee,
    totalFees,
    profit,
    margin: Math.max(0, margin)
  };
}

/**
 * Calculate Shopee pricing
 * @param {number} desiredNetPrice - What seller wants to receive
 * @param {number} cost - Product cost
 * @returns {Object} Pricing breakdown
 */
export function calculateShopeePricing(desiredNetPrice, cost = 0) {
  const result = calculateMarketplacePrice(desiredNetPrice, cost, MARKETPLACE_FEES?.shopee);
  return {
    ...result,
    channel: 'shopee'
  };
}

/**
 * Calculate Mercado Livre pricing
 * @param {number} desiredNetPrice - What seller wants to receive
 * @param {number} cost - Product cost
 * @param {string} listingType - 'Clássico' or 'Premium'
 * @returns {Object} Pricing breakdown
 */
export function calculateMercadoLivrePricing(desiredNetPrice, cost = 0, listingType = 'Clássico') {
  const fees = listingType === 'Premium' 
    ? MARKETPLACE_FEES?.mercadolivre?.premium 
    : MARKETPLACE_FEES?.mercadolivre?.classico;
  
  const result = calculateMarketplacePrice(desiredNetPrice, cost, fees);
  return {
    ...result,
    channel: 'mercadolivre',
    listingType
  };
}

/**
 * Calculate Amazon pricing
 * @param {number} desiredNetPrice - What seller wants to receive
 * @param {number} cost - Product cost
 * @returns {Object} Pricing breakdown
 */
export function calculateAmazonPricing(desiredNetPrice, cost = 0) {
  const result = calculateMarketplacePrice(desiredNetPrice, cost, MARKETPLACE_FEES?.amazon);
  return {
    ...result,
    channel: 'amazon'
  };
}

/**
 * Calculate pricing for all channels
 * @param {Object} product - Product with cost and price (desired net price)
 * @returns {Object} Pricing for all channels
 */
export function calculateAllChannelPricing(product) {
  const cost = parseFloat(product?.cost) || 0;
  const price = parseFloat(product?.price) || 0;
  const listingType = product?.mercadolivreListingType || 'Clássico';
  
  return {
    shopee: calculateShopeePricing(price, cost),
    mercadolivre: calculateMercadoLivrePricing(price, cost, listingType),
    amazon: calculateAmazonPricing(price, cost)
  };
}

/**
 * Calculate kit pricing (sum of component costs)
 * @param {Array} components - Kit components with cost and quantity
 * @param {number} desiredNetPrice - What seller wants to receive for the kit
 * @returns {Object} Kit pricing for all channels
 */
export function calculateKitPricing(components, desiredNetPrice = 0) {
  const totalCost = components?.reduce((sum, comp) => {
    const cost = parseFloat(comp?.cost) || 0;
    const quantity = parseInt(comp?.quantity) || 1;
    return sum + (cost * quantity);
  }, 0);
  
  return {
    totalCost,
    shopee: calculateShopeePricing(desiredNetPrice, totalCost),
    mercadolivre: calculateMercadoLivrePricing(desiredNetPrice, totalCost, 'Clássico'),
    amazon: calculateAmazonPricing(desiredNetPrice, totalCost)
  };
}

/**
 * Format currency for display
 * @param {number} value - Numeric value
 * @returns {string} Formatted currency
 */
export function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })?.format(value || 0);
}

/**
 * Format percentage for display
 * @param {number} value - Numeric value
 * @returns {string} Formatted percentage
 */
export function formatPercentage(value) {
  return `${(value || 0)?.toFixed(2)}%`;
}

export const pricingService = {
  calculateShopeePricing,
  calculateMercadoLivrePricing,
  calculateAmazonPricing,
  calculateAllChannelPricing,
  calculateKitPricing,
  formatCurrency,
  formatPercentage,
  MARKETPLACE_FEES
};